import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { vendorApi } from "../../api/api";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import GradientButton from "../common/GradiantButton";
const documentTypes = [
  { label: "EC", value: "EC" },
  { label: "Patta", value: "Patta" },
  { label: "Other", value: "Other" },
];
const AddDocuments = () => {
  const role = useSelector((state) => state.user.role);
  const [docType, setDocType] = useState("");
  const [docName, setDocName] = useState("");
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const theme = useSelector((data) => data.themeValue.colors);
  const location = useLocation();
  const { propertyData } = location.state || {};
  const onDrop = useCallback((acceptedFiles) => {
    const pdf = acceptedFiles.find((file) => file.type === "application/pdf");
    if (pdf) setFile(pdf);
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
  });
  const handleUpload = async () => {
    if (!docType || !docName || !file) {
      setSnackbar({
        open: true,
        message: "Please fill all fields and upload a PDF file",
        severity: "error",
      });
      return;
    }
    const docPayload = {
      docType: docType,
      docOrder: 1,
      isAdditionalDocument: false,
      docName: docName,
    };
    const formData = new FormData();
    formData.append("propertyId", propertyData[0]?.Pid ?? propertyData[0]?.id);
    formData.append("Documents[]", JSON.stringify(docPayload));
    formData.append("file[0]", file);
    try {
      setIsLoading(true);
      const response = await vendorApi.addDocuments(formData);
      if (response.status === 200 || response.status === 201) {
        setSnackbar({
          open: true,
          message: "Document uploaded successfully!",
          severity: "success",
        });
        // Reset form
        setDocType("");
        setDocName("");
        setFile(null);
      } else {
        throw new Error(response?.data?.message || "Unexpected response");
      }
    } catch (error) {
      console.error(error);
      const errorMsg =
        error.response?.data?.message || error.message || "Upload failed";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Box sx={{ maxWidth: { lg: "50vw", xs: "80vw" }, margin: "auto", mt: 4, height: "80vh", padding: 5 ,marginBottom:10 }}>
      <Typography variant="h6" gutterBottom sx={{ color: theme.text, paddingBottom: 4 }}>
        Add Document
      </Typography>
      <Stack spacing={4}>
        <TextField
          label="Document Type"
          select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              color: theme.text, // Value text color
            },
            '& .MuiSelect-select': {
              color: theme.text, // Selected value color
            },
            '& .MuiInputLabel-root': {
              color: theme.text, // Label color
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.primary_color, // Focused label
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.text,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.primary_color,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.primary_color,
            },
            '& .MuiSelect-placeholder': {
              color: theme.placeholder || '#aaa', // You can define placeholder color if needed
            },
          }}
        >
          {documentTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Document Name"
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          fullWidth
          sx={{
            mb: 3,
            input: {
              color: theme.text,
            },
            label: {
              color: theme.text,
            },
            '& label.Mui-focused': {
              color: theme.primary_color,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.text,
              },
              '&:hover fieldset': {
                borderColor: theme.primary_color,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.primary_color,
              },
            },
          }}
        />
        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            border: "2px dashed #90CAF9",
            backgroundColor: isDragActive ? "#F0F8FF" : "#FAFAFA",
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <input {...getInputProps()} />
          <Typography>
            {isDragActive
              ? "Drop the PDF file here..."
              : "Drag & drop PDF here, or click to select"}
          </Typography>
          <Button variant="outlined" sx={{ mt: 1 }}>
            Select File
          </Button>
        </Paper>
        {file && (
          <List>
            <ListItem>
              <ListItemIcon>
                <PictureAsPdfIcon color="error" />
              </ListItemIcon>
              <ListItemText primary={file.name} />
            </ListItem>
          </List>
        )}
        <GradientButton
          onClick={handleUpload}
          disabled={isLoading}
          text={isLoading ? <CircularProgress size={24} color="inherit" /> : "Upload Document"}
          width="100%"
        
        />
      </Stack>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default AddDocuments;
